<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const router = useRouter();

const imports = ref([]);
const isFetching = ref(false);
let refreshTimer = null;

const copy = {
  title: 'Importações de contatos',
  description: 'Acompanhe contatos cadastrados, repetidos e com erro.',
  empty: 'Nenhuma importação encontrada.',
  refresh: 'Atualizar',
  back: 'Voltar',
  downloadErrors: 'Baixar erros',
  emptyValue: '-',
};

const columns = [
  { key: 'created_at', label: 'Data', align: 'left' },
  { key: 'status', label: 'Status', align: 'left' },
  { key: 'total_records', label: 'Total', align: 'right' },
  { key: 'processed_records', label: 'Cadastrados', align: 'right' },
  { key: 'duplicate_records', label: 'Duplicados', align: 'right' },
  { key: 'failed_records', label: 'Erro', align: 'right' },
  { key: 'file', label: 'Arquivo', align: 'right' },
];

const statusLabel = {
  pending: 'Aguardando',
  processing: 'Processando',
  completed: 'Finalizado',
  failed: 'Erro',
};

const statusClass = {
  pending: 'text-n-amber-11 bg-n-amber-3',
  processing: 'text-n-blue-11 bg-n-blue-3',
  completed: 'text-n-teal-11 bg-n-teal-3',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
};

const hasImports = computed(() => imports.value.length > 0);

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const fetchImports = async () => {
  isFetching.value = true;
  try {
    const { data } = await ContactAPI.importHistory();
    imports.value = data.payload || [];
  } finally {
    isFetching.value = false;
  }
};

onMounted(() => {
  fetchImports();
  refreshTimer = window.setInterval(fetchImports, 15000);
});

onBeforeUnmount(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 py-6 bg-n-surface-1">
      <div class="flex items-center justify-between gap-3 mx-auto max-w-5xl">
        <div class="min-w-0">
          <h1 class="text-xl font-medium truncate text-n-slate-12">
            {{ copy.title }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ copy.description }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            icon="i-lucide-refresh-cw"
            :label="copy.refresh"
            color="slate"
            size="sm"
            :is-loading="isFetching"
            @click="fetchImports"
          />
          <Button
            icon="i-lucide-arrow-left"
            :label="copy.back"
            size="sm"
            @click="router.push({ name: 'contacts_dashboard_index' })"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 pb-8">
      <div class="mx-auto max-w-5xl">
        <div
          v-if="isFetching && !hasImports"
          class="flex items-center justify-center h-48"
        >
          <Spinner class="text-n-brand" />
        </div>

        <div
          v-else-if="!hasImports"
          class="flex items-center justify-center h-48 rounded-lg border border-n-weak text-n-slate-11"
        >
          {{ copy.empty }}
        </div>

        <div v-else class="overflow-hidden rounded-lg border border-n-weak">
          <table class="w-full text-sm">
            <thead class="bg-n-alpha-2 text-n-slate-11">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="px-4 py-3 font-medium"
                  :class="column.align === 'right' ? 'text-right' : 'text-left'"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="item in imports"
                :key="item.id"
                class="bg-n-solid-1 text-n-slate-12"
              >
                <td class="px-4 py-3">{{ formatDate(item.created_at) }}</td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex rounded-md px-2 py-1 text-xs font-medium"
                    :class="statusClass[item.status]"
                  >
                    {{ statusLabel[item.status] || item.status }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right tabular-nums">
                  {{ item.total_records }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums">
                  {{ item.processed_records }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums">
                  {{ item.duplicate_records }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums">
                  {{ item.failed_records }}
                </td>
                <td class="px-4 py-3 text-right">
                  <a
                    v-if="item.failed_records_url"
                    :href="item.failed_records_url"
                    class="text-n-brand hover:underline"
                  >
                    {{ copy.downloadErrors }}
                  </a>
                  <span v-else class="text-n-slate-10">
                    {{ copy.emptyValue }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
  </section>
</template>
